import { FC } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { CustomBadgeText } from "../../../components/CustomBadgeText/CustomBadgeText";
import { IconWithBackground } from "../../../components/IconWithBackground/IconWithBackground";
import { colors } from "../../../styles/colors";
import {
  PlatformRevenue,
  formatCents,
  formatCentsCompact,
} from "../../../api/revenue";
import useStyles from "./styles";

type Props = {
  revenue?: PlatformRevenue;
};

type KpiProps = {
  icon: string;
  lightColor: string;
  darkColor: string;
  label: string;
  value: string;
  caption: string;
  trend?: number;
  trendLabel?: string;
};

const Kpi: FC<KpiProps> = ({
  icon,
  lightColor,
  darkColor,
  label,
  value,
  caption,
  trend,
  trendLabel,
}) => {
  const classes = useStyles();

  // `trend === 0` is a real, meaningful reading (flat month) and must not be
  // swallowed by a falsy check the way `trend &&` would.
  const hasTrend = typeof trend === "number";
  const trendClass = !hasTrend
    ? classes.trendFlat
    : trend! > 0
    ? classes.trendUp
    : trend! < 0
    ? classes.trendDown
    : classes.trendFlat;

  return (
    <motion.div
      className={classes.kpiCard}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7 }}
    >
      <div className={classes.kpiHead}>
        <IconWithBackground
          iconName={icon}
          backgroundColor={lightColor}
          iconColor={darkColor}
          width={"28px"}
          height={"28px"}
        />
        <CustomBadgeText
          label={label}
          backgroundColor={lightColor}
          colorText={darkColor}
        />
      </div>

      <div className={classes.kpiBody}>
        <div className={classes.kpiValue}>
          <CustomTypography text={value} variant={"h2"} />
        </div>
        <CustomTypography text={caption} variant={"default"} />
      </div>

      {hasTrend && (
        <div className={classes.kpiFooter}>
          <span className={trendClass}>
            {trend! > 0 ? "+" : ""}
            {trend!.toFixed(1)}%
          </span>
          {trendLabel && (
            <span className={classes.planMeta}>{trendLabel}</span>
          )}
        </div>
      )}
    </motion.div>
  );
};

export const RevenueInfos: FC<Props> = ({ revenue }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const currency = revenue?.currency ?? "EUR";
  const money = (cents?: number) => formatCents(cents, currency);

  const series = revenue?.revenue_evolution ?? [];
  const byPlan = revenue?.by_plan ?? [];

  // Share-of-MRR bars are relative to the largest plan, not to total MRR, so
  // the leading plan always fills its track and the others read as a clear
  // comparison. Against the total, a healthy three-plan split would render as
  // three short stubs that look like a problem.
  const topPlanMrr = byPlan.length > 0 ? byPlan[0].mrr_cents : 0;

  const hasAnyRevenue =
    (revenue?.collected_total_cents ?? 0) > 0 || (revenue?.mrr_cents ?? 0) > 0;

  return (
    <div className={classes.section}>
      <CustomTypography
        text={t("dashboard.revenue", "Revenue")}
        variant={"h4"}
        isUppercase
      />

      <div className={classes.kpiGrid}>
        <Kpi
          icon="coinEuro"
          lightColor={colors.greenLight}
          darkColor={colors.greenDark}
          label={t("dashboard.mrr", "MRR")}
          value={money(revenue?.mrr_cents)}
          caption={t(
            "dashboard.mrrCaption",
            "Monthly recurring revenue, normalised"
          )}
        />

        <Kpi
          icon="trending-up"
          lightColor={colors.lightBlue}
          darkColor={colors.darkBlue}
          label={t("dashboard.arr", "ARR")}
          value={money(revenue?.arr_cents)}
          caption={t("dashboard.arrCaption", "Annual run-rate")}
        />

        <Kpi
          icon="cards"
          lightColor={colors.lightpurple}
          darkColor={colors.darkpurple}
          label={t("dashboard.collectedThisMonth", "Collected this month")}
          value={money(revenue?.collected_this_month_cents)}
          caption={t(
            "dashboard.collectedCaption",
            "Cash received, excluding VAT"
          )}
          trend={revenue?.collected_growth_percent}
          trendLabel={t("dashboard.vsLastMonth", "vs last month")}
        />

        <Kpi
          icon="hourglassLow"
          lightColor={colors.orangeLight}
          darkColor={colors.orangeDark}
          label={t("dashboard.outstanding", "Outstanding")}
          value={money(revenue?.outstanding_cents)}
          caption={t(
            "dashboard.outstandingCaption",
            "Invoiced and awaiting payment"
          )}
        />
      </div>

      <div className={classes.splitRow}>
        <motion.div
          className={classes.panel}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
        >
          <div className={classes.kpiHead}>
            <CustomTypography
              text={t("dashboard.revenueCollected", "Revenue collected")}
              variant={"h4"}
            />
            <span className={classes.planMeta}>
              {t("dashboard.last12Months", "Last 12 months")}
            </span>
          </div>

          {hasAnyRevenue ? (
            <div className={classes.chartWrap}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series}>
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={colors.primary}
                        stopOpacity={0.35}
                      />
                      <stop
                        offset="100%"
                        stopColor={colors.primary}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    width={70}
                    tickFormatter={(v: number) =>
                      formatCentsCompact(v, currency)
                    }
                  />
                  <Tooltip
                    formatter={(v: number) => [
                      formatCents(v, currency),
                      t("dashboard.collected", "Collected"),
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke={colors.primary}
                    strokeWidth={2}
                    fill="url(#revFill)"
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={classes.emptyState}>
              <span>
                {t(
                  "dashboard.noRevenueYet",
                  "No paid invoices recorded yet."
                )}
              </span>
              <span>
                {t(
                  "dashboard.noRevenueHint",
                  "Figures appear once Stripe delivers the first invoice.paid webhook."
                )}
              </span>
            </div>
          )}
        </motion.div>

        <motion.div
          className={classes.panel}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
        >
          <CustomTypography
            text={t("dashboard.mrrByPlan", "MRR by plan")}
            variant={"h4"}
          />

          {byPlan.length > 0 ? (
            <div className={classes.planList}>
              {byPlan.map((plan) => (
                <div key={plan.slug} className={classes.planRow}>
                  <div className={classes.planRowHead}>
                    <span className={classes.planName} title={plan.name}>
                      {plan.name}
                    </span>
                    <span className={classes.planValue}>
                      {money(plan.mrr_cents)}
                    </span>
                  </div>
                  <div className={classes.planBarTrack}>
                    <div
                      className={classes.planBarFill}
                      style={{
                        width:
                          topPlanMrr > 0
                            ? `${(plan.mrr_cents / topPlanMrr) * 100}%`
                            : "0%",
                      }}
                    />
                  </div>
                  <span className={classes.planMeta}>
                    {t("dashboard.planMeta", {
                      defaultValue:
                        "{{subs}} subscriptions - {{vehicles}} vehicles",
                      subs: plan.subscriptions,
                      vehicles: plan.vehicles,
                    })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className={classes.emptyState}>
              <span>
                {t("dashboard.noActivePlans", "No paying subscriptions yet.")}
              </span>
            </div>
          )}

          <div className={classes.planList}>
            <div className={classes.planRowHead}>
              <span className={classes.planMeta}>
                {t("dashboard.payingAccounts", "Paying accounts")}
              </span>
              <span className={classes.planValue}>
                {revenue?.paying_accounts ?? 0}
              </span>
            </div>
            <div className={classes.planRowHead}>
              <span className={classes.planMeta}>
                {t("dashboard.onTrial", "On trial")}
              </span>
              <span className={classes.planValue}>
                {revenue?.trialing_accounts ?? 0}
              </span>
            </div>
            <div className={classes.planRowHead}>
              <span className={classes.planMeta}>
                {t("dashboard.pastDue", "Past due")}
              </span>
              <span className={classes.planValue}>
                {revenue?.past_due_accounts ?? 0}
              </span>
            </div>
            <div className={classes.planRowHead}>
              <span className={classes.planMeta}>
                {t("dashboard.arpa", "Average per account")}
              </span>
              <span className={classes.planValue}>
                {money(revenue?.arpa_cents)}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
